import React, {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useKeycloak} from '@react-keycloak/web';

import 'shared/styles/table.style.less';
import {AiFillDelete, AiFillEdit, AiFillEye} from 'react-icons/all';
import {
  Button,
  Col,
  Divider,
  Form,
  message,
  Modal,
  Row,
  Select,
  Space,
} from 'antd';
import AppTableContainer from '@crema/core/AppTableContainer';
import PractitionerSelect from 'pages/components/PractitionerSelect';
import {RequestContext} from './RequestContext';
import {useHealthInfo} from 'hooks/HealthHooks';
import {
  deleteRequest,
  findRequestById,
  findRequests,
} from 'services/RequestService';
import {handleError} from 'utils/MessageUtils';
import {periodTimeText} from 'utils/Utils';
import {EQUAL} from 'shared/constants/QueryModes';
import {ServiceRequestStatus, X_TOTAL_COUNT} from 'shared/constants/AppConst';

const RequestTable = () => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const navigate = useNavigate();
  const {patient} = useHealthInfo();
  const [form] = Form.useForm();
  const confirm = Modal.confirm;

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const requestContext = useContext(RequestContext);
  const {setSelectedRequest, setEditing, reloading, setReloading} =
    requestContext;

  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });
  const [loading, setLoading] = useState(false);
  const [searchValues, setSearchValues] = useState();

  const loadData = (params) => {
    const filters = [];
    if (params.filters) {
      Object.keys(params.filters).map((key) =>
        filters.push({
          fields: key,
          value: params.filters[key],
        }),
      );
    }

    if (searchValues?.status) {
      filters.push({
        fields: 'status',
        mode: EQUAL,
        value: searchValues.status,
      });
    }

    if (searchValues?.requester?.id) {
      filters.push({
        fields: 'requester.id',
        mode: EQUAL,
        value: searchValues.requester.id,
      });
    }

    if (searchValues?.performer?.id) {
      filters.push({
        fields: 'performer.id',
        mode: EQUAL,
        value: searchValues.performer.id,
      });
    }

    if (patient?.id) {
      filters.push({
        fields: 'patient.id',
        mode: EQUAL,
        value: patient.id,
      });
    }

    setLoading(true);
    findRequests(headers, {
      pagination: params.pagination,
      filters,
      sorters: params.sorters,
    })
      .then((res) => {
        setRequests(res.data);
        setPagination({
          ...params.pagination,
          total: res.headers[X_TOTAL_COUNT]
            ? parseInt(res.headers[X_TOTAL_COUNT])
            : 0,
        });
      })
      .catch((error) => handleError(message, error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (patient) {
      loadData({pagination});
    }
  }, [patient]);

  useEffect(() => {
    if (reloading) {
      loadData({pagination});
      setReloading(false);
    }
  }, [reloading]);

  useEffect(() => {
    if (searchValues) {
      loadData({
        pagination: {...pagination, current: 1, pageSize: 20},
      });
    }

    form.resetFields();
  }, [searchValues]);

  const onView = (id) => {
    navigate(`/health/requests/${id}`);
  };

  const onEdit = (id) => {
    setLoading(true);
    findRequestById(headers, id)
      .then((res) => {
        setSelectedRequest(res.data);
        setEditing(true);
      })
      .catch((error) => handleError(message, error))
      .finally(() => setLoading(false));
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const onSearch = (values) => {
    setSearchValues(values);
  };

  const onClearSearch = () => {
    setSearchValues({});
  };

  const showConfirm = (id) => {
    confirm({
      title: intl.messages['common.confirmDelete'],
      content: '',
      onOk() {
        deleteRequest(headers, id)
          .then(() => {
            message.success(intl.messages['common.deleted']);
            setReloading(true);
          })
          .catch((error) => handleError(message, error));
      },
    });
  };

  const columns = [
    {
      title: intl.messages['request.patient'],
      dataIndex: 'patient',
      key: 'patient',
      render: (patient) => patient?.name,
    },
    {
      title: intl.messages['request.speciality'],
      dataIndex: 'speciality',
      key: 'speciality',
      render: (speciality) => speciality?.name,
    },
    {
      title: intl.messages['request.symptom'],
      dataIndex: 'symptom',
    },
    {
      title: intl.messages['request.occurrenceDate'],
      dataIndex: 'occurrenceStartTime',
      key: 'occurrenceStartTime',
      render: (occurrenceStartTime, record) =>
        periodTimeText(record.occurrenceStartTime, record.occurrenceEndTime),
    },
    {
      title: intl.messages['request.status'],
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => (
        <span>
          {status
            ? intl.messages[`request.status.${status.toLowerCase()}`]
            : ''}
        </span>
      ),
    },
    {
      title: intl.messages['common.actions'],
      dataIndex: 'id',
      key: 'actions',
      className: 'customer-table-actions',
      fixed: 'right',
      render: (id, record) => (
        <Space>
          <Button
            type='primary'
            shape='circle'
            ghost
            icon={<AiFillEye />}
            onClick={() => onView(id)}
          />
          <Button
            type='primary'
            shape='circle'
            ghost
            style={{borderColor: 'green'}}
            icon={<AiFillEdit color='green' />}
            disabled={
              record.status !== ServiceRequestStatus.draft &&
              record.status !== ServiceRequestStatus.active
            }
            onClick={() => onEdit(id)}
          />
          <Button
            danger
            shape='circle'
            icon={<AiFillDelete />}
            disabled={record.status !== ServiceRequestStatus.draft}
            onClick={() => showConfirm(id)}
          />
        </Space>
      ),
    },
  ];
  return (
    <>
      <div style={{padding: 10}}>
        <Form
          layout='vertical'
          form={form}
          initialValues={searchValues}
          onFinish={onSearch}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name={['requester', 'id']}
                label={intl.messages['request.requester']}>
                <PractitionerSelect
                  onChange={(value) => {
                    form.setFieldsValue({requester: {id: value}});
                  }}
                  onClear={() => {
                    form.setFieldsValue({requester: null});
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={['performer', 'id']}
                label={intl.messages['request.performer']}>
                <PractitionerSelect
                  onChange={(value) => {
                    form.setFieldsValue({performer: {id: value}});
                  }}
                  onClear={() => {
                    form.setFieldsValue({performer: null});
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='status' label={intl.messages['request.status']}>
                <Select>
                  <Select.Option></Select.Option>
                  <Select.Option value={ServiceRequestStatus.draft}>
                    {intl.messages['request.status.draft']}
                  </Select.Option>
                  <Select.Option value={ServiceRequestStatus.active}>
                    {intl.messages['request.status.active']}
                  </Select.Option>
                  <Select.Option value={ServiceRequestStatus.completed}>
                    {intl.messages['request.status.completed']}
                  </Select.Option>
                  <Select.Option value={ServiceRequestStatus.cancel}>
                    {intl.messages['request.status.cancel']}
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={10} />
            <Col span={2}>
              <Button onClick={onClearSearch} style={{width: '100%'}}>
                {intl.messages['common.reset']}
              </Button>
            </Col>
            <Col span={2}>
              <Button type='primary' htmlType='submit' style={{width: '100%'}}>
                {intl.messages['common.search']}
              </Button>
            </Col>
            <Col span={10} />
          </Row>
        </Form>
      </div>

      <Divider />

      <AppTableContainer
        className='customer-table'
        hoverColor
        data={requests}
        columns={columns}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />
    </>
  );
};

export default RequestTable;
